<template>
    <div class="section-form mb-5">
        <!-- Cabeçalho da Seção -->
        <div class="section-header">
            <h5 :class="`text-center text-white`">
                <i :class="`${icon} mr-2 text-white`"></i>
                {{ title }}
                <span
                    v-if="showConsultationMessage"
                    class="consultation-badge"
                >
                    <i class="fas fa-eye mr-1"></i>
                    {{ consultationMessage }}
                </span>
            </h5>
        </div>

        <!-- Carrossel de Veículos -->
        <div
            class="vehicle-carousel mt-4"
            v-if="vehicles && vehicles.length > 0"
        >
            <!-- Controles do carrossel -->
            <div class="carousel-controls d-flex justify-content-between align-items-center mb-3">
                <div class="carousel-info">
                    <span class="text-muted">
                        {{ currentSlide + 1 }} de {{ totalSlides }} |
                        Total: {{ vehicles.length }} veículos
                    </span>
                </div>
                <div class="carousel-buttons">
                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm mr-2"
                        :disabled="currentSlide === 0"
                        @click="prevSlide"
                    >
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="currentSlide === totalSlides - 1"
                        @click="nextSlide"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>

            <!-- Container do carrossel -->
            <div class="carousel-container">
                <div
                    class="carousel-track"
                    :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
                >
                    <div
                        v-for="(slide, slideIndex) in vehicleSlides"
                        :key="slideIndex"
                        class="carousel-slide"
                    >
                        <div class="row">
                            <div
                                v-for="(vehicle, index) in slide"
                                :key="vehicle.id"
                                class="col-lg-4 col-md-6 mb-4 p-2"
                            >
                                <VehicleCard
                                    :vehicle="vehicle"
                                    :index="index"
                                    :is-selected="vehicle.id === selectedVehicleId"
                                    :is-consultation-only="isConsultationOnly"
                                    :requires-approver="requiresApprover"
                                    :is-not-shared="isNotShared"
                                    @select="selectVehicle"
                                    @show-details="showDetails"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Indicadores do carrossel -->
            <div class="carousel-indicators text-center mt-3" v-if="totalSlides > 1">
                <button
                    v-for="(slide, index) in totalSlides"
                    :key="index"
                    type="button"
                    class="carousel-dot"
                    :class="{ active: index === currentSlide }"
                    @click="goToSlide(index)"
                ></button>
            </div>
        </div>

        <!-- Mensagem quando não há veículos na seção -->
        <div
            v-else
            class="row mt-4"
        >
            <div class="col-12 text-center py-4">
                <div class="empty-section">
                    <i :class="`${icon} empty-icon`"></i>
                    <p class="text-muted mb-0">{{ emptyMessage }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VehicleCard from './VehicleCard.vue';

export default {
    name: 'VehicleSection',
    components: {
        VehicleCard,
    },
    props: {
        title: {
            type: String,
            required: true
        },
        icon: {
            type: String,
            default: 'fas fa-car'
        },
        color: {
            type: String,
            default: 'primary'
        },
        vehicles: {
            type: Array,
            default: () => []
        },
        selectedVehicleId: {
            type: [String, Number],
            default: ''
        },
        isConsultationOnly: {
            type: Boolean,
            default: false
        },
        requiresApprover: {
            type: Boolean,
            default: false
        },
        showConsultationMessage: {
            type: Boolean,
            default: false
        },
        consultationMessage: {
            type: String,
            default: 'Modo consulta'
        },
        isNotShared: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentSlide: 0,
            cardsPerSlide: {
                lg: 3,
                md: 2,
                sm: 1
            },
            currentBreakpoint: 'lg'
        };
    },
    computed: {
        // Mensagem quando não há veículos
        emptyMessage() {
            if (this.requiresApprover) {
                return 'Nenhum veículo compartilhado disponível no momento.';
            }
            return 'Nenhum veículo disponível neste centro de custo.';
        },

        // Número de cards por slide baseado no breakpoint atual
        currentCardsPerSlide() {
            return this.cardsPerSlide[this.currentBreakpoint];
        },

        // Divide os veículos em slides
        vehicleSlides() {
            const slides = [];
            const cardsPerSlide = this.currentCardsPerSlide;

            for (let i = 0; i < this.vehicles.length; i += cardsPerSlide) {
                slides.push(this.vehicles.slice(i, i + cardsPerSlide));
            }

            return slides;
        },

        // Total de slides
        totalSlides() {
            return this.vehicleSlides.length;
        }
    },
    mounted() {
        this.updateBreakpoint();
        window.addEventListener('resize', this.updateBreakpoint);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateBreakpoint);
    },
    watch: {
        vehicles() {
            // Reset slide quando os veículos mudarem
            this.currentSlide = 0;
        }
    },
    methods: {
        // Seleciona um veículo
        selectVehicle(vehicle) {
            this.$emit('select-vehicle', vehicle, this.requiresApprover);
        },

        // Exibe detalhes do veículo
        showDetails(vehicle) {
            this.$emit('show-details', vehicle);
        },

        // Navega para o próximo slide
        nextSlide() {
            if (this.currentSlide < this.totalSlides - 1) {
                this.currentSlide++;
            }
        },

        // Navega para o slide anterior
        prevSlide() {
            if (this.currentSlide > 0) {
                this.currentSlide--;
            }
        },

        // Vai para um slide específico
        goToSlide(index) {
            this.currentSlide = index;
        },

        // Atualiza o breakpoint baseado no tamanho da tela
        updateBreakpoint() {
            const width = window.innerWidth;

            if (width >= 992) {
                this.currentBreakpoint = 'lg';
            } else if (width >= 768) {
                this.currentBreakpoint = 'md';
            } else {
                this.currentBreakpoint = 'sm';
            }

            // Ajusta o slide atual se necessário após mudança de breakpoint
            const maxSlide = this.totalSlides - 1;
            if (this.currentSlide > maxSlide) {
                this.currentSlide = maxSlide;
            }
        }
    }
};
</script>

<style scoped>
/* Estilos do Carrossel */
.vehicle-carousel {
    position: relative;
}

.carousel-controls {
    padding: 0 10px;
}

.carousel-info {
    font-size: 0.9rem;
}

.carousel-buttons .btn {
    min-width: 38px;
    min-height: 38px;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.carousel-buttons .btn:hover:not(:disabled) {
    background-color: #FE8D18;
    color: white;
    border-color: #FE8D18;
}

.carousel-buttons .btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.carousel-container {
    overflow: hidden;
    position: relative;
    border-radius: 8px;
}

.carousel-track {
    display: flex;
    transition: transform 0.3s ease-in-out;
    width: 100%;
}

.carousel-slide {
    flex: 0 0 100%;
    width: 100%;
    padding: 0 5px;
}

.carousel-slide .row {
    margin: 0 -5px;
}

.carousel-slide .col-lg-4,
.carousel-slide .col-md-6,
.carousel-slide .mb-4 {
    padding: 0 5px;
}

.carousel-indicators {
    margin-top: 15px;
}

.carousel-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: none;
    background-color: #dee2e6;
    margin: 0 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
}

.carousel-dot:hover {
    background-color: #FE8D18;
    transform: scale(1.2);
}

.carousel-dot.active {
    background-color: #FE8D18;
    transform: scale(1.3);
}

.carousel-dot:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

/* Responsividade do carrossel */
@media (max-width: 991px) {
    .carousel-slide .col-lg-4 {
        flex: 0 0 50%;
        max-width: 50%;
    }
}

@media (max-width: 767px) {
    .carousel-slide .col-lg-4,
    .carousel-slide .col-md-6 {
        flex: 0 0 100%;
        max-width: 100%;
    }

    .carousel-controls {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .carousel-info {
        text-align: center;
        font-size: 0.8rem;
    }

    .carousel-buttons {
        display: flex;
        justify-content: center;
        gap: 10px;
    }
}

.vehicle-section {
    background-color: #ffffff;
    padding: 20px;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    width: 100%;
    max-width: 100%;
}

.section-header {
    margin: -1.3rem;
    margin-bottom: 15px;
}

.section-title {
    position: relative;
    font-weight: 600;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    border-radius: 8px !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.consultation-badge {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(255, 255, 255, 0.2);
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.empty-section {
    padding: 40px 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
}

.empty-icon {
    font-size: 2rem;
    color: #6c757d;
    margin-bottom: 10px;
    display: flex;
}

/* Cores das seções */
.bg-primary {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%) !important;
}

.bg-secondary {
    background: linear-gradient(135deg, #6c757d 0%, #545b62 100%) !important;
}

.bg-success {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%) !important;
}

.bg-info {
    background: linear-gradient(135deg, #17a2b8 0%, #117a8b 100%) !important;
}

.bg-warning {
    background: linear-gradient(135deg, #ffc107 0%, #d39e00 100%) !important;
}

.bg-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%) !important;
}

/* Responsividade */
@media (max-width: 768px) {
    .vehicle-section {
        padding: 15px;
    }

    .consultation-badge {
        position: static;
        transform: none;
        display: block;
        margin-top: 5px;
        text-align: center;
    }

    .section-title {
        padding: 15px !important;
        font-size: 1rem;
    }

    .empty-section {
        padding: 30px 15px;
    }

    .empty-icon {
        font-size: 1.5rem;
    }
}

@media (max-width: 576px) {
    .consultation-badge {
        font-size: 11px;
        padding: 1px 6px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/add-components/VehicleSection.vue